import { gameLogger } from '../../utils/GameLogger';
import { AssetLoader } from '../assets/AssetLoader';
import { SpriteTextureSystem } from '../systems/sprite/SpriteTextureSystem';
import { PixiAssets } from '../assets/PixiAssets';
import type { IAssets } from '../assets/IAssets';
import { Scene } from '../host/Scene';
import { abilityLoader } from '../systems/player/abilities/registration/abilityLoader';
import { loadAllBehaviors } from '../systems/player/abilities/BehaviorRegistry';
import { bootPathname, bootSearchParams } from '../../config/boot/bootUrl';

/**
 * PreloadScene
 * Handles asset preloading before the main game starts. Phase H10 Arc λ.4
 * routes through IAssets (PhaserAssets today; PixiAssets after the λ.7
 * default-flip). Phase H10 Arc λ.8: extends `Scene` (substrate-agnostic
 * base) instead of Phaser.Scene directly. Phase H10 Arc λ.9a: init data
 * captured via IScene.init(data) (no `this.scene.settings.data`); scene
 * transition via IScenes.start (no `this.scene.start`).
 */
export class PreloadScene extends Scene {
  private assets: IAssets | null = null;
  private preloadPromise: Promise<void> | null = null;
  private initData: Record<string, unknown> = {};

  constructor() {
    super('PreloadScene');
  }

  init(data: unknown) {
    this.initData = (data ?? {}) as Record<string, unknown>;
  }

  preload() {
    gameLogger.info('scene', '🔄 PreloadScene: Starting asset generation...');

    // Phase H10 Arc λ.4 — IAssets-driven preload. Phaser's loader-event
    // channel is no longer driving the batch; we own it through
    // IAssets.onProgress so the splash UI works under Pixi-default mode
    // too.
    //
    // Phase H10 Arc λ.9 Stage B4 phase 2 — switched from PhaserAssets to
    // PixiAssets. The Phaser texture cache is no longer the source-of-truth
    // for gameplay rendering (post-λ.7 Pixi default). PixiAssets writes
    // textures into Pixi v8's Assets module-level cache where PixiSprite
    // reads from. Removes a layer of dual-cache duplication.
    const assets = new PixiAssets();
    this.assets = assets;
    this.wireProgressUI(assets);

    // Kick off the batch. We don't await here — Phaser's preload()
    // expects synchronous return — but we stash the promise so create()
    // can await it before transitioning. Phase H10 Arc λ.9 Stage B4
    // phase 3 — `this` (Phaser.Scene) arg dropped; AssetLoader uses
    // the IAssets reference for SpriteTextureSystem too.
    this.preloadPromise = AssetLoader.getInstance().preload(assets);
  }

  async create() {
    // Wait for the IAssets-driven batch to finish before transitioning.
    if (this.preloadPromise) {
      await this.preloadPromise;
    }

    gameLogger.info('scene', '✅ PreloadScene: Assets ready. Transitioning to GameScene.');

    // Initialize SpriteTextureSystem (creates fallback textures).
    // Phase H10 Arc λ.9 Stage B4 phase 2 — pass IAssets directly instead
    // of a Phaser.Scene. SpriteTextureSystem is now substrate-agnostic.
    if (this.assets) {
      SpriteTextureSystem.getInstance(this.assets);
    }

    // Load ability behavior classes in small chunks between rAF frames so the
    // heavy ability-behavior import tree does not produce a single pre-room
    // v8.evaluateModule freeze.
    const boot = (window as any).__GAME_BOOT__;
    if (boot) boot.stage = 'Loading ability behaviors...';
    await loadAllBehaviors(3);

    // Register only what this class and session can show before GameScene
    // (registering all nine classes at load cost a ~180 ms pre-room freeze).
    const requestedClass = (this.initData.playerClass as string | undefined) ?? 'warrior';
    const playerClass = requestedClass.trim().toLowerCase() || 'warrior';
    let sessionMovementBar = false;
    try {
      const mapId = bootSearchParams().get('map') ?? '';
      sessionMovementBar =
        sessionStorage.getItem('helix_peaceful') === '1' ||
        sessionStorage.getItem('helix_biome_tour') === '1' ||
        bootPathname() === '/gmrace' ||
        mapId.startsWith('gm_race_');
    } catch { /* storage/URL unavailable */ }

    // One loader owns the order (action-bar plan phase 1): behaviors, then the
    // authored modules this class and session can show, then TOML generics.
    try {
      const result = await abilityLoader.boot({ playerClass, sessionMovementBar });
      if (boot) {
        boot.abilityRegistration = {
          status: 'registered',
          className: result.className,
          exportName: result.registrars.join(', '),
        };
      }
      gameLogger.info('abilities', `Ability loader booted ${playerClass}: ${result.registrars.join(', ') || 'TOML only'}`);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (boot) {
        boot.abilityRegistration = { status: 'failed', className: playerClass, error: message };
      }
      gameLogger.warn('abilities', `Ability loader failed to boot ${playerClass}: ${message}`);
    }

    // Verify critical textures are loaded
    const assetLoader = AssetLoader.getInstance();
    if (this.assets && !assetLoader.areCriticalTexturesLoaded(this.assets)) {
      gameLogger.warn('scene', '⚠️ Some critical textures are missing, but proceeding...');
      const missing = assetLoader.getMissingTextures(this.assets);
      gameLogger.debug('scene', `Missing textures: ${missing.join(', ')}`);
    }

    // Phase H10 Arc β.2: assets are ready, but GameScene is loaded lazily
    // after the server connection is established to avoid a pre-room
    // v8.evaluateModule freeze from its large import tree.
    if (boot) {
      boot.stage = 'Waiting for server...';
    }
    gameLogger.info('scene', '✅ PreloadScene complete; waiting for server before GameScene');
  }

  /** Phase H10 Arc λ.4 — pump IAssets progress reports into the splash UI's
   *  global hook. Replaces the old `this.load.on('progress')` channel so
   *  the boot bar works equivalently under Phaser- and Pixi-default modes. */
  private wireProgressUI(assets: IAssets): void {
    assets.onProgress((value: number) => {
      const boot = (window as any).__GAME_BOOT__;
      if (boot) {
        boot.progress = value;
        boot.stage = value < 1 ? 'Loading Assets...' : 'Finalizing...';
      }
    });
  }
}
